import { useState } from "react";

import { Grid, InputAdornment } from "@mui/material";

import { editObjectData } from "@aclymatepackages/array-immutability-helpers";
import { TextField } from "@aclymatepackages/atoms";
import { numbersRegExpTest } from "@aclymatepackages/reg-exp";

import Currencies from "../../atoms/inputs/Currencies";
import SpendBasedVendorInputs from "../vendors/SpendBasedVendorInputs";

import { fetchOurApi } from "../../../helpers/utils/apiCalls";

const fetchCurrencyConversion = async (date, currency, amount) =>
  await fetchOurApi({
    path: "/transactions/convert-currency",
    method: "POST",
    data: { date, currency, amount: Number(amount) },
    callback: (res) => res,
  });

const SpendBasedInputBlock = ({
  value,
  setValue,
  isValueDisabled,
  areNaicsInputsDisabled,
  editVendor,
  vendorCategoryData,
  knownVendorNaicsTitle,
  currency,
  setCurrency,
  disabled,
}) => {
  return (
    <Grid item container spacing={2} direction="column">
      <Grid item container spacing={2} wrap="nowrap" alignItems="center">
        <Grid item xs={7}>
          <TextField
            value={value}
            setValue={setValue}
            label="Amount"
            InputProps={{
              startAdornment: (
                <InputAdornment>
                  {currency === "USD" ? "$" : currency}
                </InputAdornment>
              ),
            }}
            disabled={isValueDisabled || disabled}
          />
        </Grid>
        <Grid item xs={5}>
          <Currencies
            value={currency}
            setValue={setCurrency}
            disabled={isValueDisabled || disabled}
          />
        </Grid>
      </Grid>
      <SpendBasedVendorInputs
        editVendor={editVendor}
        disabled={areNaicsInputsDisabled || disabled}
        questionText="What did you buy and where did you buy it?"
        overrideNaicsTitle={knownVendorNaicsTitle}
        {...vendorCategoryData}
      />
    </Grid>
  );
};

const useSpendBasedInput = ({
  transaction = {},
  onSave,
  setCalcLoading = () => {},
}) => {
  const {
    amount: existingAmount = 0,
    dollarAmount: existingDollarAmount = 0,
    originalAmount: existingOriginalAmount,
    originalCurrency: existingCurrency = "USD",
    vendorId,
    source,
    date,
    emissionData,
    status,
  } = transaction;
  const {
    naicsCode: transactionNaicsCode,
    naicsTitle: transactionNaicsTitle,
    scopeThreeCategory: transactionScopeThreeCategory,
    tonsCo2ePerDollar: transactionTonsCo2ePerDollar,
  } = emissionData || {};

  const [amount, setAmount] = useState(
    existingOriginalAmount ?? (existingDollarAmount || existingAmount)
  );
  const [currency, setCurrency] = useState(existingCurrency || "USD");
  const [vendorCategoryData, setVendorCategoryData] = useState(
    {
      scopeThreeCategory: transactionScopeThreeCategory,
      naicsCode: transactionNaicsCode,
      naicsTitle: transactionNaicsTitle,
      tonsCo2ePerDollar: transactionTonsCo2ePerDollar,
    } || {}
  );

  const editVendorCategoryData = (field) => (value) =>
    editObjectData(setVendorCategoryData, field, value);

  const {
    naicsTitle = transactionNaicsTitle,
    naicsCode = transactionNaicsCode,
    tonsCo2ePerDollar = transactionTonsCo2ePerDollar,
    scopeThreeCategory = transactionScopeThreeCategory,
  } = vendorCategoryData || {};

  const tonsCo2e = Math.abs(amount * tonsCo2ePerDollar);

  const onTransactionSave = async () => {
    const isNonUsdCurrency = currency && currency !== "USD";

    if (!isNonUsdCurrency) {
      return onSave({
        tonsCo2e,
        amount,
        emissionData: {
          naicsCode,
          naicsTitle,
          tonsCo2ePerDollar,
          scopeThreeCategory,
        },
      });
    }

    setCalcLoading(true);
    const { usdAmount, exchangeRate } = await fetchCurrencyConversion(
      date,
      currency,
      amount
    );
    setCalcLoading(false);

    return onSave({
      tonsCo2e: Math.abs(usdAmount * tonsCo2ePerDollar),
      amount: usdAmount,
      originalAmount: Number(amount),
      originalCurrency: currency,
      exchangeRate,
      emissionData: {
        naicsCode,
        naicsTitle,
        tonsCo2ePerDollar,
        scopeThreeCategory,
      },
    });
  };
  const saveEnabled = naicsCode && amount && numbersRegExpTest(amount);

  const isValueDisabled = source !== "manual-entry" && !!vendorId;

  const inputBlock = (
    <SpendBasedInputBlock
      value={amount}
      setValue={setAmount}
      currency={currency}
      setCurrency={setCurrency}
      isValueDisabled={isValueDisabled}
      areNaicsInputsDisabled={!!vendorId && scopeThreeCategory && naicsCode}
      editVendor={editVendorCategoryData}
      vendorCategoryData={{
        naicsTitle,
        naicsCode,
        tonsCo2ePerDollar,
        scopeThreeCategory,
      }}
      originalNaicsTitle={transactionNaicsTitle}
      disabled={status === "locked"}
    />
  );

  return {
    inputBlock,
    onTransactionSave,
    saveEnabled,
    tonsCo2e,
  };
};
export default useSpendBasedInput;
